/**
 * CollaborationProvider - Provides real-time collaboration context to the app
 *
 * Wraps the app and automatically manages workspace subscriptions for real-time
 * synchronization of collections, requests, environments, and presence.
 */

import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useStore } from '../../stores';
import { useAuth } from '../auth/AuthProvider';
import {
  eventClient,
  type CollaborationEvent,
  type ConnectionState,
  publishCollectionCreated,
  publishCollectionUpdated,
  publishCollectionDeleted,
  publishFolderCreated,
  publishFolderUpdated,
  publishFolderDeleted,
  publishRequestDeleted,
  publishEnvironmentCreated,
  publishEnvironmentUpdated,
  publishEnvironmentDeleted,
} from './eventClient';
import { generateId } from '../utils';
import type { Collection, Folder, Environment } from '../../stores';

interface CollaborationContextValue {
  connectionState: ConnectionState;
  isConnected: boolean;
  // Presence data
  activeUsers: Map<string, { userId: string; userName?: string; editingRequestId?: string }>;
}

const CollaborationContext = createContext<CollaborationContextValue | null>(null);

export function useCollaborationContext() {
  const context = useContext(CollaborationContext);
  if (!context) {
    // Return a default value if not wrapped in provider (for backwards compatibility)
    return {
      connectionState: 'disconnected' as ConnectionState,
      isConnected: false,
      activeUsers: new Map(),
    };
  }
  return context;
}

interface CollaborationProviderProps {
  children: React.ReactNode;
}

export function CollaborationProvider({ children }: CollaborationProviderProps) {
  const { user } = useAuth();
  const [connectionState, setConnectionState] = useState<ConnectionState>('disconnected');
  const [activeUsers, setActiveUsers] = useState<Map<string, { userId: string; userName?: string; editingRequestId?: string }>>(new Map());

  // Get store state and actions
  const activeWorkspaceId = useStore((state) => state.activeWorkspaceId);
  const addCollection = useStore((state) => state.addCollection);
  const updateCollection = useStore((state) => state.updateCollection);
  const deleteCollection = useStore((state) => state.deleteCollection);
  const addFolder = useStore((state) => state.addFolder);
  const updateFolder = useStore((state) => state.updateFolder);
  const deleteFolder = useStore((state) => state.deleteFolder);
  const deleteRequest = useStore((state) => state.deleteRequest);
  const addEnvironment = useStore((state) => state.addEnvironment);
  const updateEnvironment = useStore((state) => state.updateEnvironment);
  const deleteEnvironment = useStore((state) => state.deleteEnvironment);

  // Track previous workspace to handle switching
  const prevWorkspaceRef = useRef<string | null>(null);

  // Set user ID on event client
  useEffect(() => {
    if (user?.userId) {
      eventClient.setCurrentUserId(user.userId);
    }
  }, [user?.userId]);

  // Subscribe to workspace events when workspace changes
  useEffect(() => {
    // Use a default workspace ID if none is selected (for local-only mode)
    const workspaceId = activeWorkspaceId || 'local-workspace';

    // If workspace changed, unsubscribe from previous
    if (prevWorkspaceRef.current && prevWorkspaceRef.current !== workspaceId) {
      eventClient.unsubscribeFromWorkspace(prevWorkspaceRef.current);
    }

    // Skip subscription if user is not authenticated
    if (!user) {
      return;
    }

    // Subscribe to connection state changes
    const unsubscribeState = eventClient.onConnectionStateChange((state) => {
      setConnectionState(state);
      console.log('Collaboration connection state:', state);
    });

    // Subscribe to workspace and presence, then announce presence
    const setupSubscriptions = async () => {
      try {
        // Subscribe to workspace first
        await eventClient.subscribeToWorkspace(workspaceId);

        // Subscribe to presence
        await eventClient.subscribeToPresence(workspaceId);

        // Now announce presence (channels are connected)
        await eventClient.publishPresence(workspaceId, 'joined', {
          userName: user.username,
        });
      } catch (error) {
        console.error('Failed to setup collaboration subscriptions:', error);
      }
    };

    setupSubscriptions();
    prevWorkspaceRef.current = workspaceId;

    return () => {
      // Announce leaving on cleanup
      eventClient.publishPresence(workspaceId, 'left');
      unsubscribeState();
    };
  }, [activeWorkspaceId, user]);

  // Handle incoming collaboration events
  useEffect(() => {
    const handleEvent = (event: CollaborationEvent) => {
      console.log('Processing collaboration event:', event.type, event);

      switch (event.type) {
        case 'collection:created':
          if ('collectionId' in event && event.data) {
            addCollection({
              id: event.collectionId,
              name: event.data.name || 'Untitled',
              description: event.data.description,
              workspaceId: event.workspaceId,
              folders: [],
              requests: [],
            });
          }
          break;

        case 'collection:updated':
          if ('collectionId' in event && event.data) {
            updateCollection(event.collectionId, event.data);
          }
          break;

        case 'collection:deleted':
          if ('collectionId' in event) {
            deleteCollection(event.collectionId);
          }
          break;

        case 'folder:created':
          if ('collectionId' in event && 'folderId' in event && event.data) {
            addFolder(event.collectionId, {
              id: event.folderId,
              name: event.data.name || 'New Folder',
              parentId: event.data.parentId,
              requests: [],
            });
          }
          break;

        case 'folder:updated':
          if ('collectionId' in event && 'folderId' in event && event.data) {
            updateFolder(event.collectionId, event.folderId, event.data);
          }
          break;

        case 'folder:deleted':
          if ('collectionId' in event && 'folderId' in event) {
            deleteFolder(event.collectionId, event.folderId);
          }
          break;

        case 'request:deleted':
          if ('collectionId' in event && 'requestId' in event) {
            deleteRequest(event.collectionId, event.requestId, event.folderId || null);
          }
          break;

        case 'environment:created':
          if ('environmentId' in event && event.data) {
            addEnvironment({
              id: event.environmentId,
              name: event.data.name || 'New Environment',
              variables: [],
              isGlobal: false,
            });
          }
          break;

        case 'environment:updated':
          if ('environmentId' in event && event.data) {
            updateEnvironment(event.environmentId, event.data);
          }
          break;

        case 'environment:deleted':
          if ('environmentId' in event) {
            deleteEnvironment(event.environmentId);
          }
          break;

        case 'presence:joined':
          setActiveUsers((prev) => {
            const next = new Map(prev);
            next.set(event.userId, {
              userId: event.userId,
              userName: event.userName,
            });
            return next;
          });
          break;

        case 'presence:left':
          setActiveUsers((prev) => {
            const next = new Map(prev);
            next.delete(event.userId);
            return next;
          });
          break;

        case 'presence:editing':
          setActiveUsers((prev) => {
            const next = new Map(prev);
            const existing = next.get(event.userId);
            next.set(event.userId, {
              ...existing,
              userId: event.userId,
              editingRequestId: event.editingRequestId,
            });
            return next;
          });
          break;
      }
    };

    const unsubscribe = eventClient.onEvent(handleEvent);
    return () => unsubscribe();
  }, [
    addCollection,
    updateCollection,
    deleteCollection,
    addFolder,
    updateFolder,
    deleteFolder,
    deleteRequest,
    addEnvironment,
    updateEnvironment,
    deleteEnvironment,
  ]);

  const value: CollaborationContextValue = {
    connectionState,
    isConnected: connectionState === 'connected',
    activeUsers,
  };

  return (
    <CollaborationContext.Provider value={value}>
      {children}
    </CollaborationContext.Provider>
  );
}

// Hook to publish collection changes
export function useCollaborationPublish() {
  const { user } = useAuth();
  const activeWorkspaceId = useStore((state) => state.activeWorkspaceId);

  const workspaceId = activeWorkspaceId || 'local-workspace';
  const userId = user?.userId || 'anonymous';

  return {
    publishCollectionCreated: useCallback(
      (collectionId: string, data: { name: string; description?: string }) => {
        publishCollectionCreated(workspaceId, userId, collectionId, data);
      },
      [workspaceId, userId]
    ),
    publishCollectionUpdated: useCallback(
      (collectionId: string, data: { name?: string; description?: string }) => {
        publishCollectionUpdated(workspaceId, userId, collectionId, data);
      },
      [workspaceId, userId]
    ),
    publishCollectionDeleted: useCallback(
      (collectionId: string) => {
        publishCollectionDeleted(workspaceId, userId, collectionId);
      },
      [workspaceId, userId]
    ),
    publishFolderCreated: useCallback(
      (collectionId: string, folderId: string, data: { name: string; parentId?: string }) => {
        publishFolderCreated(workspaceId, userId, collectionId, folderId, data);
      },
      [workspaceId, userId]
    ),
    publishFolderUpdated: useCallback(
      (collectionId: string, folderId: string, data: { name?: string }) => {
        publishFolderUpdated(workspaceId, userId, collectionId, folderId, data);
      },
      [workspaceId, userId]
    ),
    publishFolderDeleted: useCallback(
      (collectionId: string, folderId: string) => {
        publishFolderDeleted(workspaceId, userId, collectionId, folderId);
      },
      [workspaceId, userId]
    ),
    publishRequestDeleted: useCallback(
      (collectionId: string, requestId: string, folderId?: string) => {
        publishRequestDeleted(workspaceId, userId, collectionId, requestId, folderId);
      },
      [workspaceId, userId]
    ),
    publishEnvironmentCreated: useCallback(
      (environmentId: string, data: { name: string }) => {
        publishEnvironmentCreated(workspaceId, userId, environmentId, data);
      },
      [workspaceId, userId]
    ),
    publishEnvironmentUpdated: useCallback(
      (environmentId: string, data: { name?: string }) => {
        publishEnvironmentUpdated(workspaceId, userId, environmentId, data);
      },
      [workspaceId, userId]
    ),
    publishEnvironmentDeleted: useCallback(
      (environmentId: string) => {
        publishEnvironmentDeleted(workspaceId, userId, environmentId);
      },
      [workspaceId, userId]
    ),
  };
}
